import { useCallback, useEffect, useRef } from 'react';
import type React from 'react';
import { LONG_PRESS_MS, LONG_PRESS_SLOP_PX } from '@/modules/config';

export interface LongPressHandlers {
  onPointerDown: (event: React.PointerEvent) => void;
  onPointerMove: (event: React.PointerEvent) => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
  onPointerLeave: () => void;
  onContextMenu: (event: React.MouseEvent) => void;
  onClick: () => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
}

/**
 * A tap and a long-press on the same element, kept apart: a press held for
 * `LONG_PRESS_MS` fires `onLongPress` and swallows the click that
 * follows it; anything shorter is a tap. A finger that drifts is scrolling,
 * so it fires neither. A right-click, the context-menu key and Shift+F10 stand
 * in for the long-press where there is no finger, and Enter or Space for the
 * tap.
 */
export function useLongPress(
  onLongPress: () => void,
  onTap: () => void
): LongPressHandlers {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);

  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    start.current = null;
  }, []);

  useEffect(() => cancel, [cancel]);

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      fired.current = false;
      if (event.button !== 0) return;
      start.current = { x: event.clientX, y: event.clientY };
      timer.current = setTimeout(() => {
        fired.current = true;
        timer.current = null;
        onLongPress();
      }, LONG_PRESS_MS);
    },
    [onLongPress]
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent) => {
      if (!start.current) return;
      const dx = event.clientX - start.current.x;
      const dy = event.clientY - start.current.y;
      if (Math.hypot(dx, dy) > LONG_PRESS_SLOP_PX) {
        fired.current = true;
        cancel();
      }
    },
    [cancel]
  );

  const onContextMenu = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      cancel();
      if (fired.current) return;
      fired.current = true;
      onLongPress();
    },
    [cancel, onLongPress]
  );

  const onClick = useCallback(() => {
    if (fired.current) {
      fired.current = false;
      return;
    }
    onTap();
  }, [onTap]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onTap();
      } else if (
        event.key === 'ContextMenu' ||
        (event.key === 'F10' && event.shiftKey)
      ) {
        event.preventDefault();
        onLongPress();
      }
    },
    [onLongPress, onTap]
  );

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onPointerLeave: cancel,
    onContextMenu,
    onClick,
    onKeyDown,
  };
}
